import type { Container, MaskData } from 'pixi.js';
import { Config } from '../../video-data/config';
import type { TextAsset } from '../text/TextAsset';
import { BaseGroup } from '../BaseGroup';
import type { CanvasTextAsset } from '../canvasText/asset/CanvasTextAsset';


export class SlideRenderScene extends BaseGroup {
    override _build() {
        this._buildTextInput();
    }

    /**
     * We turn on & off the mask while rendering scene previews. We do NOT want to emit a `childUpdated` event (which
     * is the usual behaviour) because then the rendering system recognizes that something changed during rendering
     * and then will re-render.
     * This override enforces that the `mask` property here (which isn't serialized or externally read – it's transitory
     * state!) doesn't emit a redraw.
     */
    override set mask(mask: Container | MaskData) {
        if (super.mask === mask) {
            return;
        }
        // turn off redraw listening
        const initialAutoDraw = this.autoDraw;
        const initialRequiresRedraw = this.requiresRedraw;
        this.autoDraw = false;

        super.mask = mask;

        // restore drawing
        this.requiresRedraw = initialRequiresRedraw;
        this.autoDraw = initialAutoDraw;
    }

    override get mask() {
        return this._mask;
    }

    /**
     * Update any text asset that is being edited.
     *
     * The scope of this update only includes text assets that are direct descendants of the scene
     * or any text asset nested in an AssetGroup that is a direct descandant of the scene
     */
    _buildTextInput() {
        const sceneAsset = this._childAssets.find((asset) => asset.name === 'slideScene') as BaseGroup;
        const textAssetsByGroup: Array<Array<TextAsset>> = sceneAsset
            .getAssetsByType(Config.GROUP_ASSET_TYPE, { deep: false })
            .map(assetGroup => {
                const textAssetList = assetGroup.getAssetsByType(Config.TEXT_ASSET_TYPE) as Array<TextAsset>;
                return textAssetList;
            });
        const canvasTextAssetsByGroup: Array<Array<CanvasTextAsset>> = sceneAsset
            .getAssetsByType(Config.GROUP_ASSET_TYPE, { deep: false })
            .map(assetGroup => {
                const textAssetList = assetGroup.getAssetsByType(Config.CANVAS_TEXT_ASSET_TYPE);
                return textAssetList;
            });
        const textAssetsInGroups: Array<TextAsset> = textAssetsByGroup.flat();
        const canvasAssetsInGroups: Array<CanvasTextAsset> = canvasTextAssetsByGroup.flat();
        const standaloneTextAssets: Array<TextAsset> =
            sceneAsset.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: false }) as Array<TextAsset>;

        const standaloneCanvasTextAssets: Array<CanvasTextAsset> = sceneAsset.getAssetsByType(Config.CANVAS_TEXT_ASSET_TYPE, { deep: false });

        [...textAssetsInGroups, ...canvasAssetsInGroups, ...standaloneTextAssets, ...standaloneCanvasTextAssets].forEach(textAsset => {
            textAsset.updateTextInput();
        });
    }

    /**
     * The Slide has two subtrees: direct children of the Slide, and children of a subslide.
     *
     * Depending on what was updated, we may want to redraw all subslides or just a subset of them:
     *
     * 1. Only children of subslides were updated: we want to only redraw the affected subslides
     * 2. A child of the slide was updated: we want to redraw the entire slide (all subslides)
     */
    override _updateEvent() {
        const sceneChildrenRequestingRedraw = this.childrenRequestingRedraw.some((child) => {
            return child.name === 'slideScene';
        });
        const subslideManager = this.childrenRequestingRedraw.find((child) => {
            return child.name === 'subslideManager_1';
        });

        let subslidesRequestingRedraw = [];
        if (subslideManager) {
            subslidesRequestingRedraw = subslideManager.childrenRequestingRedraw;
        }

        if (sceneChildrenRequestingRedraw || subslidesRequestingRedraw.length === 0) {
            // either the entire slide needs to be redrawn, or we need to be redrawn because
            // of a property on the Slide itself.
            // We emit the event not pointing to any subslide in particular:
            this.updateEventSource.emit('childUpdated', this.name);
        }
        else {
            // only subslides are requesting redraw. Emit one event for each subslide that
            // must be redrawn.
            subslidesRequestingRedraw.forEach((subslide) => {
                this.updateEventSource.emit('childUpdated', this.name, subslide.index);
            });
        }
    }
}
